import * as ServerTypes from "@/models/types";
import { useUserSlice } from "@/models/useUserSlice";
import { __DEV__ } from "@/constants";
import { useAuth } from "./useAuth";

let serverUrl = "https://eznote.ai/api";
// let serverUrl = "https://eznoteai-api-test.dkt201474-com.workers.dev";

if (__DEV__) {
  serverUrl = "http://localhost:8787";
}

export const parseJwt = (token: string): ServerTypes.AuthUser => {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  return JSON.parse(jsonPayload);
};

const getTokenAsync = (): string => {
  let token = useUserSlice.getState().userToken;

  if (useAuth.isAuthenticated()) {
    return token;
  }

  return "";
};

export const cipher = (jsonData: { [key: string]: any }): string => {
  const hexBuff = Buffer.from(JSON.stringify(jsonData));
  const hexData = hexBuff.toString("hex");

  const base64Buff = Buffer.from(hexData);
  return base64Buff.toString("base64");
};

export const authenticateAsync = async (
  params: ServerTypes.ApiRequestAuthenticate["payload"]
): Promise<ServerTypes.ApiResponseAuthenticate> => {
  try {
    const requestPayload: ServerTypes.ApiRequest = {
      action: "Authenticate",
      payload: params,
    };

    return await fetch(serverUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ event: requestPayload }),
    }).then((res) => res.json());
  } catch (e) {
    __DEV__ && console.log(e);
  }
};

export const noteAsync = async (
  params: ServerTypes.ApiRequestNote["payload"]
): Promise<ServerTypes.ApiResponseNote> => {
  try {
    const token = getTokenAsync();
    if (!token) return;

    const requestPayload: ServerTypes.ApiRequest = {
      action: "Note",
      payload: params,
    };

    return await fetch(serverUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({ event: requestPayload }),
    }).then((res) => res.json());
  } catch (e) {
    __DEV__ && console.log(e);
  }
};

export const subscriptionAsync = async (
  params: ServerTypes.ApiRequestSubscription["payload"]
): Promise<ServerTypes.ApiResponseSubscription> => {
  const token = getTokenAsync();
  if (!token) return;

  try {
    const requestPayload: ServerTypes.ApiRequest = {
      action: "Subscription",
      payload: params,
    };

    return await fetch(serverUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({ event: requestPayload }),
    }).then((res) => res.json());
  } catch (e) {
    __DEV__ && console.log(e);
  }
};

export const userAsync = async (
  params: ServerTypes.ApiRequestUser["payload"]
): Promise<ServerTypes.ApiResponseUser> => {
  try {
    const token = getTokenAsync();
    if (!token) return;

    const requestPayload: ServerTypes.ApiRequest = {
      action: "User",
      payload: params,
    };

    return await fetch(serverUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({ event: requestPayload }),
    }).then((res) => res.json());
  } catch (e) {
    __DEV__ && console.log(e);
  }
};

export const folderAsync = async (
  params: ServerTypes.ApiRequestFolder["payload"]
): Promise<ServerTypes.ApiResponseFolder> => {
  try {
    const token = getTokenAsync();
    if (!token) return;

    const requestPayload: ServerTypes.ApiRequest = {
      action: "Folder",
      payload: params,
    };

    return await fetch(serverUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({ event: requestPayload }),
    }).then((res) => res.json());
  } catch (e) {
    __DEV__ && console.log(e);
  }
};

export const transcribeAsync = async (
  params: ServerTypes.ApiRequestTranscribe["payload"]
): Promise<ServerTypes.ApiResponseTranscribe> => {
  try {
    const token = getTokenAsync();
    if (!token) return;

    const requestPayload: ServerTypes.ApiRequest = {
      action: "Transcribe",
      payload: params,
    };

    return await fetch(serverUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({ event: requestPayload }),
    }).then((res) => res.json());
  } catch (e) {
    __DEV__ && console.log(e);
  }
};

export const searchAsync = async (
  params: ServerTypes.ApiRequestSearch["payload"]
): Promise<ServerTypes.ApiResponseSearch> => {
  try {
    const token = getTokenAsync();
    if (!token) return;

    const requestPayload: ServerTypes.ApiRequest = {
      action: "Search",
      payload: params,
    };

    return await fetch(serverUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({ event: requestPayload }),
    }).then((res) => res.json());
  } catch (e) {
    __DEV__ && console.log(e);
  }
};

export const uploadFileAsync = async (params: {
  key: string;
  body?: ArrayBuffer;
  signal?: AbortSignal;
  partNumber?: string;
  uploadId?: string;
  parts?: { PartNumber: number; ETag: string }[];
}): Promise<ServerTypes.ApiResponseUploadFile> => {
  try {
    const token = getTokenAsync();
    if (!token) return;

    const url = new URL(`${serverUrl}/upload-file`);
    url.searchParams.append("action", "UploadFile");
    url.searchParams.append("key", params.key);
    if (params.partNumber) {
      url.searchParams.append("partNumber", params.partNumber);
    }
    if (params.parts) {
      url.searchParams.append("parts", JSON.stringify(params.parts));
    }
    if (params.uploadId) {
      url.searchParams.append("uploadId", params.uploadId);
    }

    return await fetch(url, {
      signal: params.signal,
      method: "POST",
      headers: {
        Authorization: token,
        Accept: "application/json",
        "Content-Type": "application/octet-stream",
      },
      body: params.body,
    }).then((res) => res.json());
  } catch (e) {
    __DEV__ && console.log(e);
    return { error: e, ok: false, action: "UploadFile" };
  }
};
